import React from 'react'
import { motion } from 'framer-motion'
import { FaInstagram } from 'react-icons/fa'

interface FooterLogo {
  text: string
  subtext: string
  icon: string
}

interface FooterLink {
  label: string
  href: string
}

interface FooterContact {
  address: string
  phone: string
  email: string
}

interface BusinessHours {
  monToWed: string
  thuToSat: string
  sunday: string
}

interface SocialMedia {
  platform: string
  url: string
  icon: string
}

interface FooterData {
  logo: FooterLogo
  description: string
  quickLinks: FooterLink[]
  services: string[]
  contact: FooterContact
  businessHours: BusinessHours
  copyright: string
  socialMedia: SocialMedia[]
}

interface FooterProps {
  data: FooterData
}

const Footer: React.FC<FooterProps> = ({ data }) => {
  return (
    <footer className="bg-gray-900 dark:bg-custom-dark text-white py-10 px-4 sm:px-6 md:px-12 border-t border-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">

        <div className="grid gap-10 md:grid-cols-4 mb-12">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/logo.jpg" 
                alt="Barber Innovations Logo" 
                className="w-[clamp(2.5rem,6vw,3.5rem)] h-[clamp(2.5rem,6vw,3.5rem)] rounded-full object-cover shadow-md"
              />
              <div>
                <h3 className="font-bold text-white break-words text-[clamp(1rem,1.4vw,1.15rem)]">
                  {data.logo.text}
                </h3>
                <p className="uppercase tracking-widest text-gray-400 break-words text-[clamp(0.6rem,0.8vw,0.7rem)]">
                  {data.logo.subtext}
                </p>
              </div>
            </div>

            <p className="text-gray-300 break-words leading-[1.7] text-[clamp(0.85rem,1.1vw,0.95rem)]">
              {data.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-6 text-[clamp(0.95rem,1.3vw,1.05rem)]">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {data.quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-300 break-words transition-colors hover:text-primary text-[clamp(0.85rem,1.1vw,0.95rem)]"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6 text-[clamp(0.95rem,1.3vw,1.05rem)]">
              Services
            </h4>
            <ul className="space-y-3">
              {data.services.map((service, index) => (
                <li
                  key={index}
                  className="text-gray-300 break-words text-[clamp(0.85rem,1.1vw,0.95rem)]"
                >
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-6 text-[clamp(0.95rem,1.3vw,1.05rem)]">
              Contact Info
            </h4>

            <div className="space-y-3 leading-[1.6] text-gray-300 break-words text-[clamp(0.85rem,1.1vw,0.95rem)]">
              <p>{data.contact.address}</p>
              <p>{data.contact.phone}</p>
              <p>{data.contact.email}</p>

              <div className="mt-6">
                <h5 className="font-medium mb-3 text-[clamp(0.8rem,1vw,0.9rem)]">
                  Business Hours
                </h5>
                <div className="space-y-1 text-gray-400 text-[clamp(0.7rem,0.9vw,0.8rem)]">
                  <p>{data.businessHours.monToWed}</p>
                  <p>{data.businessHours.thuToSat}</p>
                  <p>{data.businessHours.sunday}</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 break-words text-[clamp(0.75rem,1vw,0.85rem)]">
              {data.copyright}
            </p>

            <div className="flex space-x-6">
              {data.socialMedia.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  aria-label={social.platform}
                  className="text-gray-400 hover:text-primary transition-colors"
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {social.icon === 'photo_camera' ? (
                    <FaInstagram className="text-[clamp(1.1rem,1.5vw,1.25rem)]" />
                  ) : (
                    <span className="material-symbols-outlined text-[clamp(1.1rem,1.5vw,1.25rem)]">
                      {social.icon}
                    </span>
                  )}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Powered by SnapMenuAI */}
          <p className="text-center text-gray-500 text-[clamp(0.7rem,0.9vw,0.8rem)] mt-4">
            Powered by <span className="text-primary font-semibold">SnapMenuAI</span>
          </p>
        </div>

      </div>
    </footer>
  )
}

export default Footer
